/**
 * Company role catalog loaded at login. Source of truth for code → label/icon/color.
 */

import { create } from 'zustand';
import type { Role } from '@/types/role.types';

/** Zustand catalog of all company roles, keyed by lowercase code and by id. */
interface RoleCatalogStore {
  roles: Role[];
  byCode: Record<string, Role>;
  byId: Record<string, Role>;
  isLoaded: boolean;
  setRoles: (roles: Role[]) => void;
  clear: () => void;
}

/** Index roles by lowercase code and by id for catalog lookups. */
function indexRoles(roles: Role[]): Pick<RoleCatalogStore, 'roles' | 'byCode' | 'byId'> {
  const byCode: Record<string, Role> = {};
  const byId: Record<string, Role> = {};
  for (const role of roles) {
    byCode[role.code.toLowerCase()] = role;
    byId[role.id] = role;
  }
  return { roles, byCode, byId };
}

/** Session-wide role catalog. Not persisted — refreshed on every login. */
export const useRoleCatalogStore = create<RoleCatalogStore>()((set) => ({
  roles: [],
  byCode: {},
  byId: {},
  isLoaded: false,

  setRoles: (roles) => {
    set({ ...indexRoles(roles), isLoaded: true });
  },

  clear: () => {
    set({ roles: [], byCode: {}, byId: {}, isLoaded: false });
  },
}));
